import {
  EmptyState,
  ListX,
  Table,
  TableProps,
  TableReloadHandle,
  TableSortOption,
} from "@surya-digital/leo-reactjs-material-ui";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { GetEmailCycleRecipientsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailCycleRecipientsRPC";
import { TypesSortOrder } from "@pulse/pulse-rpcs/lib/types/typesSortOrder";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { useTypography } from "@surya-digital/leo-reactjs-material-ui";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import {
  RouteSearchParam,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { getValidatedLeoUUIDFromSearchParam } from "../../../utils/getValidatedLeoUUIDFromSearchParam";
import { getSortOrder } from "../../../utils/getSortOrder";
import { EMAIL_CYCLE_RECIPIENTS_PER_PAGE } from "../../stores/EmailCycleRecipientsStore";
import {
  useEmailCycleDetailsStore,
  useEmailCycleRecipientsStore,
  useRemoveEmailCycleRecipientStore,
} from "../../stores/Hooks";
import {
  EmailCycleRecipientsTableColumn,
  getEmailCycleRecipientsTableCells,
  getEmailCycleRecipientsTableHeader,
} from "./headersAndCells/EmailCycleRecipientTableHeadersAndCells";
import { useSearchParams } from "react-router-dom";
import { PreviewEmailDialog } from "../dialogs/PreviewEmailDialog";
import { InvestigationSummaryDialog } from "../dialogs/InvestigationSummaryDialog";
import { EmailStatus } from "@pulse/pulse-rpcs/lib/types/emailStatus";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";
import {
  EmailCycleRecipientsTableFilter,
  EmailCycleRecipientsTableFilterType,
} from "./filters/EmailCycleRecipientsTableFilter";
import { ConfirmRemoveEmailCycleRecipientDialog } from "../dialogs/ConfirmRemoveEmailCycleRecipientDialog";
import { useSignInStore } from "../../../auth/store/hook";
import { UserPrivileges } from "../../../store/user/UserPrivileges";

const TABLE_MAX_WIDTH = "1376px";

interface SortOrderAndColumn {
  order: TypesSortOrder.TypesSortOrder;
  column: GetEmailCycleRecipientsRPC.RequestEnums.SortColumn.SortColumn;
}

interface PreviewEmailRecipient {
  emailId: string;
  status: EmailStatus.EmailStatus;
}

export const EmailCycleRecipientsTable: React.FC = observer(() => {
  const [searchParams] = useSearchParams();
  const [openUnexpectedErrorDialog, setOpenUnexpectedErrorDialog] =
    useState(false);
  const [previewEmailRecipient, setPreviewEmailRecipient] =
    useState<PreviewEmailRecipient | null>(null);
  const [investigationSummaryEmailId, setInvestigationSummaryEmailId] =
    useState<string | null>(null);

  const { t } = useTranslation();
  const typography = useTypography();
  const navigateTo = useRouteNavigator();
  const emailCycleRecipientsTableRef = useRef<TableReloadHandle | null>(null);

  const signInStore = useSignInStore();
  const emailCycleDetailsStore = useEmailCycleDetailsStore();
  const emailCycleRecipientsStore = useEmailCycleRecipientsStore();
  const removeEmailCycleRecipientStore = useRemoveEmailCycleRecipientStore();

  const scheduleId = getValidatedLeoUUIDFromSearchParam(
    searchParams,
    RouteSearchParam.ScheduleId,
  )?.uuid;

  const paginationOption: TableProps["paginationOption"] = {
    itemsPerPage: EMAIL_CYCLE_RECIPIENTS_PER_PAGE,
    getPageIndicatorText: (startItem, endItem, totalItems): string =>
      t("common.paginationIndicationText", {
        startItem,
        endItem,
        totalItems,
      }),
  };

  const viewOverrides: TableProps["viewOverrides"] = {
    empty: {
      icon: (
        <EmptyState
          icon={<ListX />}
          title={t("emailCycles.emptyRecipientsMessage")}
          description={EMPTY_CHARACTER}
          width="100%"
        />
      ),
      message: EMPTY_CHARACTER,
    },
    loading: {
      message: t("common.searchTableLoadingState"),
    },
  };

  const isQueued =
    emailCycleDetailsStore.status === EmailCycleStatus.EmailCycleStatus.QUEUED;
  const hideRemoveRecipientButton =
    !isQueued ||
    !signInStore.isPrivilegeGranted(UserPrivileges.processEmailCycle);

  const getSortColumn = (columnId: string): SortOrderAndColumn["column"] => {
    switch (columnId) {
      case EmailCycleRecipientsTableColumn.AddedOn:
        return GetEmailCycleRecipientsRPC.RequestEnums.SortColumn.SortColumn
          .ADDED_ON;
      default:
        throw new Error(`Invalid columnId: ${columnId}`);
    }
  };

  const getSortOrderAndColumn = (
    sort: TableSortOption | undefined,
  ): SortOrderAndColumn => {
    if (!sort) {
      return {
        order: TypesSortOrder.TypesSortOrder.DESCENDING,
        column:
          GetEmailCycleRecipientsRPC.RequestEnums.SortColumn.SortColumn
            .ADDED_ON,
      };
    }

    return {
      order: getSortOrder(sort.order),
      column: getSortColumn(sort.id),
    };
  };

  const onTableOptionsChange: TableProps<EmailCycleRecipientsTableFilterType>["onTableOptionsChange"] =
    async (options, setTotalItems) => {
      if (!scheduleId) return [];

      const { order, column } = getSortOrderAndColumn(options.sort);
      const filter = options.filter ?? { value: EMPTY_CHARACTER };
      const pageIndex = options.pageNumber ?? 1;

      await emailCycleRecipientsStore
        .getEmailCycleRecipients(
          scheduleId,
          filter.value,
          pageIndex,
          order,
          column,
          true,
        )
        .catch(() => setOpenUnexpectedErrorDialog(true));

      if (!emailCycleRecipientsStore.getEmailCycleRecipientsRPCError) {
        setTotalItems(emailCycleRecipientsStore.totalItems);
        return emailCycleRecipientsStore.recipients.map((recipient) => ({
          id: recipient.id,
          cells: getEmailCycleRecipientsTableCells(
            t,
            typography,
            recipient,
            (emailId, status) =>
              setPreviewEmailRecipient({
                emailId,
                status,
              }),
            setInvestigationSummaryEmailId,
            isQueued,
            removeEmailCycleRecipientStore.setRecipientDetails,
          ),
        }));
      }

      switch (emailCycleRecipientsStore.getEmailCycleRecipientsRPCError) {
        case GetEmailCycleRecipientsRPC.RPCError.InvalidScheduleId:
          navigateTo.pageNotFound();
          break;
        case GetEmailCycleRecipientsRPC.RPCError.InvalidPageIndex:
          await emailCycleRecipientsStore.getEmailCycleRecipients(
            scheduleId,
            EMPTY_CHARACTER,
            Math.max(1, pageIndex - 1),
            order,
            column,
            true,
          );
          break;
        default:
          emailCycleRecipientsStore.getEmailCycleRecipientsRPCError satisfies never;
      }

      return [];
    };

  return (
    <Stack width="100%" maxWidth={TABLE_MAX_WIDTH}>
      <Table<EmailCycleRecipientsTableFilterType>
        name="emailCycleRecipientsTable"
        ref={emailCycleRecipientsTableRef}
        header={getEmailCycleRecipientsTableHeader(
          t,
          hideRemoveRecipientButton,
        )}
        paginationOption={paginationOption}
        onTableOptionsChange={onTableOptionsChange}
        viewOverrides={viewOverrides}
        leftPinnedColumnIds={[]}
        rightPinnedColumnIds={[EmailCycleRecipientsTableColumn.Actions]}
        filterOption={{
          initialFilterValue: { value: EMPTY_CHARACTER },
          filterComponent: (filter, setFilter) => (
            <EmailCycleRecipientsTableFilter
              filter={filter}
              setFilter={setFilter}
            />
          ),
        }}
      />
      {openUnexpectedErrorDialog && (
        <UnexpectedErrorDialog
          message={t(
            "emailCycles.getEmailCycleRecipientsUnexpectedErrorMessage",
          )}
          onClickDone={(): void => setOpenUnexpectedErrorDialog(false)}
        />
      )}
      {previewEmailRecipient && (
        <PreviewEmailDialog
          emailId={previewEmailRecipient.emailId}
          cycleStatus={emailCycleDetailsStore.status}
          recipientStatus={previewEmailRecipient.status}
          onClose={(): void => setPreviewEmailRecipient(null)}
          onCloseAfterResendAttempt={(): void => {
            setPreviewEmailRecipient(null);
            emailCycleRecipientsTableRef.current?.reload();
          }}
        />
      )}
      {investigationSummaryEmailId && (
        <InvestigationSummaryDialog
          emailId={investigationSummaryEmailId}
          onClose={(): void => setInvestigationSummaryEmailId(null)}
        />
      )}
      {removeEmailCycleRecipientStore.recipientDetails && scheduleId && (
        <ConfirmRemoveEmailCycleRecipientDialog
          scheduleId={scheduleId}
          handleDialogClose={() => {
            removeEmailCycleRecipientStore.resetStore();
            emailCycleRecipientsTableRef.current?.reload();
          }}
        />
      )}
    </Stack>
  );
});
